/*
  The label preview's geometry (D5), ported from the portal's label renderer
  (card-grading-admin-ui src/pages/labelling/LabelFrontSVG.tsx and LabelBackSVG.tsx) so
  the public preview sits every mark where the engraved label does. Pure: positions, sizes
  and path data in, nothing from React or the DOM, so it is unit-tested here.

  **What is ported exactly:** the 385×116 label, the 6-unit border inset and 2-unit frame,
  the gap in the top border for the corner mark (wider for "PCG CERTIFIED"), the back's
  bottom-centre gap, the arc shapes and where they sit, the grade box at GRADE_X, and every
  text line's position, size, weight and the portal's own size step-downs.

  **What is approximated:** the portal outlines its text with opentype.js from shipped
  Arial and Noto Sans JP files, and shrinks a line to its maxWidth by measuring the glyphs.
  Those fonts are not ours to serve from a public site, so the preview sets plain SVG text
  in the visitor's Arial (or Helvetica, or a Japanese system face) and ESTIMATES each
  line's width from a per-character advance table measured off the portal's own font
  files. Only a line estimated wider than its room is fitted, by the portal's own rule (the
  size scaled by room / width), plus a textLength of exactly the room, so the visitor's real
  font cannot overrun it.

  The grade lettering is the one exception. The portal's "Arial-Black.ttf" is in fact
  Arial MT Std ExtraBold Condensed (its name table says so), which visitors do not have and
  which real Arial Black is a third wider than. So that text is set in a condensed or plain
  bold Arial and ALWAYS held by textLength to the width the portal's face gives it.
*/

import { ARC_ORIG_H, ARC_ORIG_W, ARC_SUBGRADE_PATHS } from './labelArt';

/* ── The label (LabelFrontSVG / LabelBackSVG constants) ─────────────────────────────── */

export const LW = 385;
export const LH = 116;
export const BORDER_W = 2;
export const BORDER_INSET = 6;

/*
  The three finishes (Robert, 2026-10-26; replaces batch 4's gold-above-9):

    gold          Flawless only. Robert's PDF: a brushed gold face, the frame, mark,
                  lettering and arcs in silver, and the grade panel in silver with the
                  grade in gold on it.
    black-gold    Pristine 10, Gem Mint 10 and 9.5. The black label with its parts in gold
                  and the grade knocked out of a gold panel, as the portal prints it (its
                  #D0AA59 accent above 9).
    black-silver  9 and below, and Authentic. The black label with its parts in silver
                  (Robert, 2026-10-25: "the label area is black and should only be silver
                  where it is already silver").

  ONE silver: SILVER_HI and SILVER_LO are the final-grade tile's stops, --pcg-silver-hi and
  --pcg-silver-lo in index.css (a test holds the two equal). ONE gold: GOLD_HI is the
  brand gold --pcg-gold-chrome, the same in light and dark, and GOLD_LO the default
  --pcg-gold-dark the tile's gradient falls to. Like the tile, a metal panel runs its HI
  flat to 55% and then down to its LO. The label is the slab's own metal, so none of these
  flip with the theme.

  Each finish names:
    face        the ground: three stops, top, 55% and bottom
    brush       how strongly the brushed streaks show (0 none: a flat face, no filter)
    edge        the hairline round the whole label, so a metal face holds its shape on
                any page; null for the black face, which needs none
    ink         the frame, the PCG mark, the card lines, the arcs and the back's QR
    inkStroke   a thin outline under the ink (paint-order stroke), as the PDF's lettering has
    panel       the grade panel's two stops, HI and LO
    gradeInk    the grade name and figure on the grade panel
    gradeStroke their outline
*/
export const SILVER_HI = '#E2E5EB'; /* rgb(226 229 235) */
export const SILVER_LO = '#AAB0BC'; /* rgb(170 176 188) */
export const GOLD_HI = '#DEBB68'; /* rgb(222 187 104), --pcg-gold-chrome */
export const GOLD_LO = '#C89440'; /* rgb(200 148 64), :root --pcg-gold-dark */

export type FinishPalette = {
  face: readonly [string, string, string];
  brush: number;
  edge: string | null;
  ink: string;
  inkStroke: string | null;
  panel: readonly [string, string];
  gradeInk: string;
  gradeStroke: string | null;
};

const BLACK_FACE = ['#000000', '#000000', '#000000'] as const;

export const FINISHES: Record<'gold' | 'black-gold' | 'black-silver', FinishPalette> = {
  gold: {
    face: ['#D8BB78', '#C6A462', '#B08C4E'],
    brush: 0.34,
    edge: '#8F6F35',
    ink: SILVER_HI,
    inkStroke: '#7D838E',
    panel: [SILVER_HI, SILVER_LO],
    /*
      A deeper gold with no outline. At preview size the grade name is about 8px tall, and
      the PDF's brown outline round a light gold smeared it (Robert, 2026-10-26: "can you
      make this less fuzzy"). #A47A2C keeps it gold and gives it about 3:1 on the silver.
    */
    gradeInk: '#A47A2C',
    gradeStroke: null,
  },
  'black-gold': {
    face: BLACK_FACE,
    brush: 0,
    edge: null,
    ink: GOLD_HI,
    inkStroke: null,
    panel: [GOLD_HI, GOLD_LO],
    gradeInk: '#000000',
    gradeStroke: null,
  },
  'black-silver': {
    face: BLACK_FACE,
    brush: 0,
    edge: null,
    ink: SILVER_HI,
    inkStroke: null,
    panel: [SILVER_HI, SILVER_LO],
    gradeInk: '#000000',
    gradeStroke: null,
  },
};

/* Front */
export const GRADE_X = 309;
export const ARC_BASE_Y = 99;
export const ARC_CX = [55.5, 123.83, 192.17, 260.5] as const;
export const ARC_SCALED_W = 56;
export const ARC_SCALED_H = 29;
export const LOGO_GAP_END = 34;
export const CERTIFIED_GAP_END = 111.25;
export const CERTIFIED_X = 37;
export const CERTIFIED_Y = 7.87 * 1.8 + 1.1;
/* The mark's cap height over Proxima Nova Black's cap ratio, as the portal sizes it. */
export const CERTIFIED_FONT_SIZE = ((7.87 - 2.72) * 1.8) / 0.704415;
/* "CERTIFIED" measures 71.25 in the portal's brand face; the heavy face is held to that. */
export const CERTIFIED_W = 71.25;
export const NAME_MAX_W = GRADE_X - BORDER_INSET - 8;
export const GRADE_MAX_W = LW - GRADE_X - BORDER_INSET - 4;
export const GCX = (GRADE_X + LW - BORDER_INSET) / 2;
export const CCX = (BORDER_INSET + GRADE_X) / 2;
export const GRADE_BOX = {
  x: GRADE_X,
  y: BORDER_INSET,
  w: LW - GRADE_X - BORDER_INSET,
  h: LH - 2 * BORDER_INSET,
} as const;
export const FRONT_LOGO_TRANSFORM = 'translate(1.1, 1.1) scale(1.8)';

/* Back */
export const PADDING_X = 24;
export const QR_SIZE = 65;
export const QR_X = LW - PADDING_X - QR_SIZE;
export const QR_Y = 20;
export const TEXT_Y = 29;
export const BACK_MAX_W = QR_X - PADDING_X - 10;
export const BACK_LOGO_TRANSFORM = `translate(${LW / 2 - 16.5}, ${LH - BORDER_INSET - 10}) scale(1.8)`;

/* ── Text ───────────────────────────────────────────────────────────────────────────── */

export type Weight = 'regular' | 'bold' | 'heavy';

/*
  The portal's three faces are Arial, Arial Bold and the condensed extra-bold it calls
  "bolder", with Noto Sans JP behind them for Japanese. The stacks name the nearest faces a
  visitor has, then the system Japanese faces, then the generic family, whose fallback
  reaches a CJK face on any system that has one. (No family name here may contain the
  word the D4 test bans from the markup, which rules out naming Noto Sans JP itself.)
*/
const CJK_FALLBACK = '"Hiragino Sans", "Hiragino Kaku Gothic ProN", "Yu Gothic", Meiryo';
export const FONT_FAMILY: Record<Weight, string> = {
  regular: `Arial, Helvetica, ${CJK_FALLBACK}, sans-serif`,
  bold: `Arial, Helvetica, ${CJK_FALLBACK}, sans-serif`,
  heavy: `"Arial Narrow", Arial, Helvetica, ${CJK_FALLBACK}, sans-serif`,
};
export const FONT_WEIGHT: Record<Weight, number> = { regular: 400, bold: 700, heavy: 800 };

/*
  Arial advances in em, by character class, averaged off the portal's Arial-Regular,
  Arial-Bold and Arial-Black (condensed) files with opentype.js. Full-width (CJK, kana,
  full-width forms) is a whole em in every Japanese face. "Gem Mint" in the heavy face
  estimates 59.1 against a measured 58.7.
*/
const NARROW = new Set(Array.from("il.,:;'!|Ijft()[]/ "));
const WIDE = new Set(Array.from('mwMW@%'));
const ADVANCE: Record<Weight, { narrow: number; wide: number; upper: number; other: number }> = {
  regular: { narrow: 0.269, wide: 0.873, upper: 0.676, other: 0.541 },
  bold: { narrow: 0.302, wide: 0.885, upper: 0.688, other: 0.569 },
  heavy: { narrow: 0.288, wide: 0.768, upper: 0.532, other: 0.49 },
};

const isFullWidth = (code: number): boolean =>
  (code >= 0x1100 && code <= 0x115f) ||
  (code >= 0x2e80 && code <= 0xa4cf) ||
  (code >= 0xac00 && code <= 0xd7a3) ||
  (code >= 0xf900 && code <= 0xfaff) ||
  (code >= 0xfe30 && code <= 0xfe4f) ||
  (code >= 0xff00 && code <= 0xff60) ||
  (code >= 0xffe0 && code <= 0xffe6) ||
  code >= 0x20000;

export const estimateTextWidth = (text: string, fontSize: number, weight: Weight, letterSpacing = 0): number => {
  const table = ADVANCE[weight];
  const chars = Array.from(text);
  const em = chars.reduce((sum, ch) => {
    const code = ch.codePointAt(0) || 0;
    if (isFullWidth(code)) {
      return sum + 1;
    }
    if (NARROW.has(ch)) {
      return sum + table.narrow;
    }
    if (WIDE.has(ch)) {
      return sum + table.wide;
    }
    if (ch >= 'A' && ch <= 'Z') {
      return sum + table.upper;
    }
    return sum + table.other;
  }, 0);
  return em * fontSize + letterSpacing * Math.max(0, chars.length - 1);
};

export type TextSpec = {
  text: string;
  x: number;
  y: number;
  fontSize: number;
  weight: Weight;
  anchor: 'start' | 'middle';
  letterSpacing: number;
  /* Set only on a fitted line: the width it is held to. */
  textLength?: number;
};

type TextInput = Omit<TextSpec, 'letterSpacing' | 'textLength'> & { letterSpacing?: number; maxWidth?: number };

/*
  textToPath's maxWidth rule: a line wider than its room is scaled down to it, and only
  then. Heavy text is also pinned to its estimated width when it fits (see the top).
*/
export const fitText = ({ maxWidth, letterSpacing = 0, ...spec }: TextInput): TextSpec => {
  const width = estimateTextWidth(spec.text, spec.fontSize, spec.weight, letterSpacing);
  if (!maxWidth || width <= maxWidth) {
    return spec.weight === 'heavy'
      ? { ...spec, letterSpacing, textLength: round2(width) }
      : { ...spec, letterSpacing };
  }
  const factor = maxWidth / width;
  return {
    ...spec,
    fontSize: round2(spec.fontSize * factor),
    letterSpacing: round2(letterSpacing * factor),
    textLength: maxWidth,
  };
};

const round2 = (value: number): number => Math.round(value * 100) / 100;

/* ── Frames ─────────────────────────────────────────────────────────────────────────── */

/* The front frame, open at the top-left for the corner mark. One contour, nonzero fill. */
export const frontFramePath = (certified: boolean): string => {
  const gapEnd = certified ? CERTIFIED_GAP_END : LOGO_GAP_END;
  const i = BORDER_INSET;
  return (
    `M ${gapEnd},${i} H ${LW - i} V ${LH - i} H ${i} V 19 H ${i + 2} ` +
    `V ${LH - i - 2} H ${LW - i - 2} V ${i + 2} H ${gapEnd} Z`
  );
};

/* The back frame, open at the bottom centre for the PCG mark. Drawn evenodd. */
export const backFramePath = (): string => {
  const i = BORDER_INSET;
  const w = BORDER_W;
  return (
    `M ${LW / 2 + 18},${LH - i} H ${LW - i} V ${i} H ${i} V ${LH - i} H ${LW / 2 - 18} v -${w} ` +
    `H ${i + w} V ${i + w} H ${LW - i - w} V ${LH - i - w} H ${LW / 2 + 18} Z`
  );
};

/* ── Sub-grade arcs ─────────────────────────────────────────────────────────────────── */

const ARC_KEYS = Object.keys(ARC_SUBGRADE_PATHS)
  .map(Number)
  .sort((a, b) => a - b);

/*
  The arc drawn for a score: nothing at 0, else the score to the nearest half. The portal
  has artwork for 1–7 and 7.5–10 and draws its 1 arc for a half it has no shape for (6.5);
  here such a score takes the nearest shape BELOW it, so an arc never reads lower than the
  number printed in it by more than half a point.
*/
export const arcPathFor = (score: number): string => {
  if (!Number.isFinite(score) || score <= 0) {
    return '';
  }
  const rounded = Math.min(10, Math.round(score * 2) / 2);
  if (ARC_SUBGRADE_PATHS[rounded]) {
    return ARC_SUBGRADE_PATHS[rounded];
  }
  const below = ARC_KEYS.filter((key) => key <= rounded);
  return ARC_SUBGRADE_PATHS[below.length > 0 ? below[below.length - 1] : ARC_KEYS[0]];
};

export const arcTransform = (index: number): string => {
  const cx = ARC_CX[index];
  const sx = ARC_SCALED_W / ARC_ORIG_W;
  const sy = ARC_SCALED_H / ARC_ORIG_H;
  return `translate(${cx - ARC_SCALED_W / 2}, ${ARC_BASE_Y - ARC_SCALED_H}) scale(${sx}, ${sy})`;
};

export type SubGradeSpec = { arc: string; transform: string; score: TextSpec | null; title: TextSpec };

/* The four arcs, left to right, with the score inside each and its name under it. */
export const subGradeLayout = (subs: { title: string; value: number; text: string }[]): SubGradeSpec[] =>
  subs.slice(0, ARC_CX.length).map((sub, index) => {
    const cx = ARC_CX[index];
    const score = Number(sub.value) || 0;
    return {
      arc: arcPathFor(score),
      transform: arcTransform(index),
      score:
        score > 0 && sub.text
          ? fitText({ text: sub.text, x: cx, y: 92, fontSize: 16, weight: 'regular', anchor: 'middle' })
          : null,
      title: fitText({ text: sub.title.toUpperCase(), x: cx, y: 99, fontSize: 6, weight: 'regular', anchor: 'middle' }),
    };
  });

/* ── Faces ──────────────────────────────────────────────────────────────────────────── */

export type FrontLayout = {
  certified: boolean;
  frame: string;
  name: TextSpec | null;
  detail: TextSpec | null;
  gradeName: TextSpec | null;
  figure: TextSpec | null;
  certifiedText: TextSpec | null;
};

/*
  The front's text (buildFrontLabelContent / buildGradeBoxPath). An Authentication label
  has no sub-grades, so its card lines and grade drop to the middle of the taller space,
  and its corner mark spells out "PCG CERTIFIED" with the frame gap widened to match.
*/
export const frontLayout = ({
  cardName,
  detail,
  gradeName,
  figure,
  authentic,
}: {
  cardName: string;
  detail: string;
  gradeName: string;
  figure: string;
  authentic: boolean;
}): FrontLayout => {
  const nameY = authentic ? 55 : 41;
  const detailsY = authentic ? 73 : 57;
  const gradeScoreY = authentic ? (gradeName ? 86 : 72) : 80;
  return {
    certified: authentic,
    frame: frontFramePath(authentic),
    name: cardName
      ? fitText({
          text: cardName,
          x: CCX,
          y: nameY,
          fontSize: cardName.length > 50 ? 12 : 20,
          weight: 'bold',
          anchor: 'middle',
          maxWidth: NAME_MAX_W,
        })
      : null,
    detail: detail
      ? fitText({
          text: detail,
          x: CCX,
          y: detailsY,
          fontSize: detail.length > 50 ? 10 : 12,
          weight: 'regular',
          anchor: 'middle',
          maxWidth: NAME_MAX_W,
        })
      : null,
    /* The portal sets no maxWidth here; held to the box so a long name cannot spill out. */
    gradeName: gradeName
      ? fitText({
          text: gradeName,
          x: GCX,
          y: 44,
          fontSize: 16,
          weight: 'heavy',
          anchor: 'middle',
          letterSpacing: -0.5,
          maxWidth: GRADE_MAX_W,
        })
      : null,
    figure: figure
      ? fitText({
          text: figure,
          x: GCX,
          y: gradeScoreY,
          fontSize: 40,
          weight: 'heavy',
          anchor: 'middle',
          letterSpacing: -1,
          maxWidth: GRADE_MAX_W,
        })
      : null,
    certifiedText: authentic
      ? {
          text: 'CERTIFIED',
          x: CERTIFIED_X,
          y: round2(CERTIFIED_Y),
          fontSize: round2(CERTIFIED_FONT_SIZE),
          weight: 'heavy',
          anchor: 'start',
          letterSpacing: 0.5,
          textLength: CERTIFIED_W,
        }
      : null,
  };
};

/* The back's leading tightens as lines are added, so the last still clears the PCG mark. */
export const backLineGap = (count: number): number => (count >= 6 ? 10.5 : count === 5 ? 12 : 13.5);

export type BackLayout = { name: TextSpec | null; lines: TextSpec[]; cert: TextSpec };

/* The back's text (buildBackLabelContent): card name, then its lines, the cert under the QR. */
export const backLayout = ({
  cardName,
  lines,
  gradedId,
}: {
  cardName: string;
  lines: string[];
  gradedId: string;
}): BackLayout => {
  const active = lines.filter((line) => line.trim().length > 0);
  const gap = backLineGap(active.length);
  return {
    name: cardName
      ? fitText({
          text: cardName,
          x: PADDING_X,
          y: TEXT_Y + 4.5,
          fontSize: cardName.length > 30 ? 12 : 15,
          weight: 'bold',
          anchor: 'start',
          maxWidth: BACK_MAX_W,
        })
      : null,
    lines: active.map((text, index) =>
      fitText({
        text,
        x: PADDING_X,
        y: round2(TEXT_Y + 18.5 + index * gap),
        fontSize: 12,
        weight: 'regular',
        anchor: 'start',
        maxWidth: BACK_MAX_W,
      })
    ),
    cert: fitText({
      text: gradedId,
      x: QR_X + QR_SIZE / 2,
      y: QR_Y + QR_SIZE + 12,
      fontSize: 9,
      weight: 'regular',
      anchor: 'middle',
      letterSpacing: 0.5,
    }),
  };
};
